import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FileError } from 'react-dropzone';

import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { LMN_UPLOAD_STORE_KEY } from '@/modules/MyDocuments/myDocuments.types';
import useCreateLettersOfMedicalNecessityQuery from '@/modules/MyDocuments/queries/useCreateLettersOfMedicalNecessity.query';
import { LettersOfMedicalNecessityPopupState } from '@/modules/MyDocuments/stores/useLettersOfMedicalNecessityPopup.store';
import useFileUploadQuery from '@/modules/transaction/hooks/useFileUploadQuery';

interface Props {
  onFileUploadError?: (error?: FileError) => void
}

const useSubmitLettersOfMedicalNecessityPopup = ({ onFileUploadError }: Props) => {
  const { createDocumentId, uploadFiles } = useFileUploadQuery();
  const { createLMN } = useCreateLettersOfMedicalNecessityQuery();
  const { values: { documentId, files }, handleSetValues } = useFilesInDocumentStore(LMN_UPLOAD_STORE_KEY);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const documentIdRef = useRef<string | undefined>(documentId);
  const filesRef = useRef<typeof files>(files);

  const file = useMemo(() => files?.[0], [files]);

  useEffect(() => {
    documentIdRef.current = documentId;
  }, [documentId]);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const handleCreateLMN = useCallback(async (payload: LettersOfMedicalNecessityPopupState) => {
    let createdDocumentId = documentIdRef.current || '';
    if (!createdDocumentId) {
      createdDocumentId = await createDocumentId(payload?.documentType);
      documentIdRef.current = createdDocumentId;
      handleSetValues({ documentId: createdDocumentId });
    }

    const currentFiles = filesRef.current || [];
    const unuploadedFiles = currentFiles.filter((file) => !file.id);
    if (unuploadedFiles?.length > 0) {
      const uploadedFiles = await uploadFiles(createdDocumentId, unuploadedFiles, false, onFileUploadError);
      if (!uploadedFiles?.length) {
        return;
      }
      const normalizedFiles = uploadedFiles?.filter((file) => file.id);
      filesRef.current = normalizedFiles;
      handleSetValues({ files: normalizedFiles });
    }

    const createdLMN = await createLMN({
      documentId: createdDocumentId,
      name: payload?.name,
      description: payload?.description,
      employeeId: payload?.employeeId,
      claimIds: payload?.claimIds
    });
    return createdLMN;
  }, [createDocumentId, uploadFiles, createLMN, handleSetValues, onFileUploadError]);

  const handleSuccessfullyCreated = useCallback((createForClaimId?: string) => {
    handleAddPermanentSnackbar({
      text: createForClaimId
        ? "Document added. The claim is about to be processed."
        : `${file?.name} was added.`,
      state: createForClaimId
        ? SnackbarState.neutral
        : SnackbarState.positive,
      closeIcon: true,
      autoRemove: true,
    });
  }, [handleAddPermanentSnackbar, file?.name]);

  return {
    handleCreateLMN,
    handleSuccessfullyCreated,
  };
};

export default useSubmitLettersOfMedicalNecessityPopup;
